import { useMemo, useState } from 'react';
import type { Report } from '@forecastify/shared';
import {
  DataList,
  type DataListColumn,
  type DataListRow,
  type MenuItem,
  type SortState,
} from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { ListHeader, usePageHeader } from '../page-header';
import { listLabels } from '../delivery/clients-screen';
import { sortInMemory } from '../sort-in-memory';
import { dimensionLabel } from './report-result';
import { chartLabel } from './report-charts';
import { REPORT_TEMPLATE_IDS, templateName, type ReportTemplateId } from './report-templates';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Mentett riportok — `main.jsx` 3521–3559, `markup.html` 1277–1279.          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Six columns, two filters, three views and a search box — one `DataList`, no bespoke table.
 *
 * ⚠️⚠️ **EVERY ROW HERE IS THE CALLER'S OWN (P-3), AND THE SCREEN SAYS SO.** The mockup's list is a
 * workspace's reports because its only user is the owner; ours cannot be, and a list that silently
 * showed a subset would read as data loss. The footer note is where that is stated.
 *
 * ⚠️ **The mockup's card grid (`savedReports`) is drawn by NOTHING** — 1,047 keys and 140 of them
 * render nowhere, and that is one. Only `repListRows` is in the markup, so only the list is built.
 */

export interface ReportsScreenProps {
  /** *Riportjaim* — the same list, opened from the sidebar's Riportok heading. Marker and title. */
  readonly mine?: boolean;
  readonly reports: readonly Report[];
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly search: string;
  readonly source: string;
  readonly dimension: string;
  readonly view: string;
  readonly canManage: boolean;
  /** `report.export` — the one export the catalogue already had an opinion about. */
  readonly canExport: boolean;
  readonly onSearch: (value: string) => void;
  readonly onSource: (value: string) => void;
  readonly onDimension: (value: string) => void;
  readonly onView: (value: string) => void;
  readonly onOpen: (report: Report) => void;
  readonly onNew: () => void;
  /** 17 Oct 2026 · open the builder set up as one of the ready-made reports. */
  readonly onTemplate: (id: ReportTemplateId) => void;
  readonly onDuplicate: (report: Report) => void;
  readonly onDelete: (report: Report) => void;
  readonly onExport: (report: Report) => void;
}

export function sourceLabel(t: Translate, source: Report['definition']['source']): string {
  /* ⚠️ One literal key per source — a computed one is invisible to the catalogue check. */
  if (source === 'task') return t('report.source.task');
  if (source === 'contract') return t('report.source.contract');
  if (source === 'lineitem') return t('report.source.lineitem');
  if (source === 'occurrence') return t('report.source.occurrence');
  /* 17 Oct 2026 · Munkaidő — people's working days. */
  if (source === 'workday') return t('report.source.workday');
  /* 26 Oct 2026 · Erőforrások — the allocations, what is planned. */
  if (source === 'allocation') return t('report.source.allocation');
  return t('report.source.worklog');
}

export function metricLabel(t: Translate, metric: Report['definition']['metric']): string {
  /* One `t()` per branch with a literal key. */
  if (metric === 'count') return t('report.metric.count');
  if (metric === 'estimate') return t('report.metric.estimate');
  /* C59 · ⚠️ Two entries, and the difference is the multiplier — the hours worked beyond a
     schedule, and what is owed for them. */
  if (metric === 'overtime') return t('report.metric.overtime');
  if (metric === 'overtimeCost') return t('report.metric.overtimeCost');
  /* C60 · the two behind `report.money`. */
  if (metric === 'revenue') return t('report.metric.revenue');
  if (metric === 'budget') return t('report.metric.budget');
  /* 17 Oct 2026 · Munkaidő's four. */
  if (metric === 'expected') return t('report.metric.expected');
  if (metric === 'missing') return t('report.metric.missing');
  if (metric === 'leave') return t('report.metric.leave');
  if (metric === 'missingCost') return t('report.metric.missingCost');
  return t('report.metric.hours');
}

/**
 * The period, as a sentence.
 *
 * ⚠️ **Three states, not two** — the mockup's `repRangeNote` has the same three: no limit, an open
 * interval, and a closed range. An open end rendered as *"— 2026-08-31"* looks like a typo.
 */
export function periodLabel(t: Translate, from: string | null, to: string | null): string {
  if (from === null && to === null) return t('report.period.none');
  /* ⚠️ The wire date verbatim, mono and tabular, as every other date column in this product does.
   * A localised *"aug 19."* would be a second date format nobody else uses, and the mockup's `fmtD`
   * is its own convention rather than a decision. */
  if (from !== null && to !== null) return `${from} – ${to}`;
  if (from !== null) return t('report.period.since', { date: from });
  return t('report.period.until', { date: to ?? '' });
}

/** What the definition filters on, joined — or nothing. */
export function filterLabel(t: Translate, report: Report): string {
  const parts = [
    report.filterProjectName,
    report.filterMemberName,
    report.definition.filterMetafieldValue,
  ].filter((part): part is string => part !== null);

  return parts.length === 0 ? t('report.filters.none') : parts.join(' · ');
}

export function ReportsScreen(props: ReportsScreenProps) {
  const t = useT();
  const [sort, setSort] = useState<readonly SortState[]>([]);
  /* The shared sidebar state — null on any screen rendered outside the chrome. */
  const sidebar = useSidebarContext();
  const { reports, canManage, canExport } = props;

  /**
   * ⚠️ **The list's own header is the SHELL'S**, not a second bar under it — `markup.html` 271–275
   * puts *Új riport* on the right of the 48px chrome, after a `flex-1`, exactly as every other list
   * screen's CTA sits. This screen used to draw a `<h2>` and the button ten pixels below the title
   * the chrome had already rendered.
   */
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE CREATE ACTION IS IN THE TOOLBAR, NOT THE HEADER — item 25, 15 Sep.│
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * *"They all should look like contracts, one line with the search, not purple."* Contracts have
   * passed their create through `DataList`'s `actions` since C30 and every other list grew a purple
   * header CTA instead, so the same act looked like two different things depending on which list
   * you were standing in.
   *
   * ⚠️ **The header keeps the TITLE and nothing else.** A header action is right for a screen whose
   * primary act is not about a list — a task detail, a project — and wrong here, where the toolbar
   * already holds the search, the filters and the columns that the create belongs beside.
   */
  usePageHeader(
    () => <ListHeader title={t(props.mine === true ? 'title.myReports' : 'title.reports')} />,
    [props.mine],
  );

  const columns: DataListColumn[] = [
    { key: 'name', label: t('report.column.name'), sortKey: 'name' },
    { key: 'source', label: t('report.column.source'), width: 130, sortKey: 'source' },
    { key: 'dimension', label: t('report.column.dimension'), width: 150, sortKey: 'dimension' },
    { key: 'metric', label: t('report.column.metric'), width: 170, sortKey: 'metric' },
    { key: 'period', label: t('report.column.period'), width: 190, sortKey: 'period' },
    { key: 'filters', label: t('report.column.filters'), width: 200 },
  ];

  /**
   * ⚠️⚠️ **SORTED FROM THE ROW'S OWN FIELDS, BEFORE THE CELLS ARE BUILT.** A cell carries a
   * translated label and a formatted figure; ordering those sorts by the words rather than by the
   * thing they describe.
   */
  const rows: DataListRow[] = useMemo(
    () =>
      sortInMemory(reports, sort, {
        name: (one) => one.name,
        /* ⚠️ The definition holds them, not the report row itself. */
        source: (one) => one.definition.source,
        dimension: (one) => one.definition.dimension,
        metric: (one) => one.definition.metric,
        period: (one) => one.definition.periodFrom ?? '',
      }).map((report) => ({
        id: report.id,
        cells: {
          name: {
            icon: 'chart-column' as const,
            text: report.name,
            bold: true,
            /* ⚠️ The chart shape as a sub-line, which is the mockup's own — it is the one part of a
             * definition you cannot infer from the other five columns. */
            sub: chartLabel(t, report.definition.chart),
          },
          source: {
            badge: {
              label: sourceLabel(t, report.definition.source),
              variant: 'secondary' as const,
            },
          },
          dimension: {
            text:
              report.definition.dimension === 'metafield' && report.dimensionMetafieldName !== null
                ? report.dimensionMetafieldName
                : dimensionLabel(t, report.definition),
          },
          metric: { text: metricLabel(t, report.definition.metric) },
          period: {
            text: periodLabel(t, report.definition.periodFrom, report.definition.periodTo),
            mono: true,
            muted: true,
          },
          filters: {
            text: filterLabel(t, report),
            muted:
              report.filterProjectName === null &&
              report.filterMemberName === null &&
              report.definition.filterMetafieldValue === null,
          },
        },
      })),
    [reports, sort, t],
  );

  const rowMenu = (id: string): MenuItem[] => {
    const report = reports.find((candidate) => candidate.id === id);
    if (report === undefined) return [];

    const pinned = sidebar?.isReportPinned(report.id) ?? false;

    const items: MenuItem[] = [
      {
        label: t('report.action.open'),
        icon: 'arrow-right',
        onSelect: () => {
          props.onOpen(report);
        },
      },
      /**
       * ⚠️ **Pinning needs nothing but `report.view`**, because it arranges the caller's own
       * sidebar and P-3 already means a report is only ever theirs. Absent entirely when there is no
       * sidebar to pin to — see `useSidebarContext`.
       */
      ...(sidebar === null
        ? []
        : [
            {
              label: pinned ? t('report.action.unpin') : t('report.action.pin'),
              icon: 'star' as const,
              onSelect: () => {
                sidebar.setReportPinned(report.id, !pinned);
              },
            },
          ]),
    ];

    /* ⚠️ **The CSV export item, which C15 left out until T-10's shared path existed.** It is now
     * `useExport`'s, so this row, the builder's kebab and the report screens all go through one
     * endpoint — which is the whole point of T-10. */
    if (canExport) {
      items.push({
        label: t('report.action.export'),
        icon: 'download',
        onSelect: () => {
          props.onExport(report);
        },
      });
    }

    if (!canManage) return items;

    items.push({ type: 'separator' });
    items.push({
      label: t('report.action.duplicate'),
      icon: 'copy',
      onSelect: () => {
        props.onDuplicate(report);
      },
    });
    items.push({
      label: t('report.action.delete'),
      icon: 'trash',
      /* ⚠️ `destructive` marks a menu ITEM, which the kit allows — what it reserves for irreversible
       * financial deletions is the filled danger BUTTON. */
      destructive: true,
      onSelect: () => {
        props.onDelete(report);
      },
    });
    return items;
  };

  return (
    <div
      data-screen={props.mine === true ? 'my-reports' : 'reports'}
      className="flex min-h-0 flex-1 flex-col"
    >
      {/* ⚠️ 17 Oct 2026 · no `p-5`: the list runs edge to edge, as every other list screen does. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DataList
          columnsKey="reports"
          card={{
            title: 'name',
            context: 'source',
            defaults: { details: ['dimension', 'metric', 'period', 'filters'] },
          }}
          isLoading={props.loading === true}
          actions={
            canManage
              ? [
                  /* 17 Oct 2026 · the ready-made reports — Kockázatkezelés's three among them. */
                  {
                    label: t('report.action.fromTemplate'),
                    icon: 'copy' as const,
                    variant: 'outline' as const,
                    items: REPORT_TEMPLATE_IDS.map((templateId) => ({
                      label: templateName(t, templateId),
                      onSelect: () => {
                        props.onTemplate(templateId);
                      },
                    })),
                  },
                  {
                    label: t('report.action.new'),
                    icon: 'plus' as const,
                    primary: true,
                    onSelect: props.onNew,
                  },
                ]
              : []
          }
          columns={columns}
          sort={sort}
          onSortChange={setSort}
          rows={rows}
          labels={listLabels(t)}
          search={props.search}
          onSearchChange={props.onSearch}
          views={[
            { value: 'all', label: t('report.view.all') },
            { value: 'worklog', label: t('report.view.worklog') },
            { value: 'task', label: t('report.view.task') },
          ]}
          view={props.view}
          onViewChange={props.onView}
          filters={[
            {
              key: 'source',
              label: t('report.column.source'),
              value: props.source,
              options: [
                { value: '', label: t('common.all') },
                { value: 'worklog', label: t('report.source.worklog') },
                { value: 'task', label: t('report.source.task') },
              ],
              onChange: props.onSource,
            },
            {
              key: 'dimension',
              label: t('report.column.dimension'),
              value: props.dimension,
              options: [
                { value: '', label: t('common.all') },
                { value: 'project', label: t('report.dim.project') },
                { value: 'client', label: t('report.dim.client') },
                { value: 'assignee', label: t('report.dim.assignee') },
                { value: 'status', label: t('report.dim.status') },
                { value: 'metafield', label: t('report.dim.metafield') },
              ],
              onChange: props.onDimension,
            },
          ]}
          rowMenu={rowMenu}
          onRowClick={(id) => {
            const report = reports.find((candidate) => candidate.id === id);
            if (report !== undefined) props.onOpen(report);
          }}
          empty={{
            icon: 'chart-column',
            title: t('report.emptyList.title'),
            body: t('report.emptyList.body'),
            actionLabel: canManage ? t('report.action.new') : undefined,
            onAction: canManage ? props.onNew : undefined,
          }}
          /* ⚠️ P-3, stated where somebody would otherwise wonder where their colleague's reports
           * went — a list that silently showed a subset would read as data loss. */
        />
      </div>
    </div>
  );
}
